/**
 * Escala de z-index del design system v4 — fuente única de verdad de la pila vertical.
 *
 * INVARIANTE INNEGOCIABLE: lo OPERATIVO (pedidos) SIEMPRE gana. El toast de pedido
 * entrante (NotificationCenterV2, `z-[99]`, bottom-0 right-0 en desktop / w-full móvil)
 * queda por encima de TODA la UI de Paulina. Por eso:
 *
 *     pedido (99)  >  Paulina sheet/panel (95)  >  Paulina pill/coachmark (80)
 *
 * Nunca usar el `z-140/z-250` del molde de sheets v4 (QuickPresentationsSheet,
 * MobileTopBarV4 branch-sheet) para una superficie de Paulina: taparía el toast.
 *
 * Estos valores son los MISMOS números que ya viven inline en Tailwind (`z-[80]`,
 * `z-[95]`) y en el toast (`z-[99]`); este módulo los centraliza para que la
 * invariante quede documentada en un solo lugar y no diverja al editar componentes.
 *
 * Overlays interruptivos (modales, prompts, banners): NO uses un z ad-hoc — regístralos en el
 * OverlayGate (`src/context/overlayGate.tsx`, `useOverlaySlot`/`useOperationalYield`) y toma el z
 * de `Z.announcementModal`/`Z.overlayPrompt`/`Z.overlayBackdrop`. Ver `DESIGN.md` §"Overlays
 * interruptivos".
 */
export const Z = {
  /**
   * Vista previa del tablero de Pantallas a pantalla completa (`BoardPreviewOverlay`). Hoy la lanza
   * la PÁGINA del editor de playlist (`PlaylistEditorPage`), no un modal: el editor dejó de ser modal
   * justamente porque su backdrop tapaba el toast de pedido durante una tarea de minutos.
   *
   * El 145 sigue vigente por dos razones: (a) tiene que ganarle al header móvil `MobileTopBarV4`
   * (z-index 100 inline) para que su botón de cerrar sea alcanzable, y (b) `ScreensModal` (130) sigue
   * vivo para los OTROS diálogos del módulo (crear pantalla, vincular, borrar), que también pueden
   * abrir contenido encima.
   *
   * NO rompe el invariante "lo operativo gana": no es un prompt interruptivo — solo existe tras un tap
   * explícito en "Ver vista previa", vive dentro del flujo de configuración y se desmonta al cerrarlo
   * (X o Esc) o al navegar, así que nunca queda co-visible con el toast de pedido esperando atención.
   *
   * 145 y no 140: el 140 es el molde de sheets v4 (`QuickPresentationsSheet`, inline) y empatar con él
   * dejaría el orden del DOM decidiendo cuál gana en vez de una decisión de diseño.
   */
  boardPreview: 145,
  /**
   * Modal de anuncio interruptivo (CommunicationsModal, curado por backend). Debe quedar por
   * ENCIMA del header de la app — móvil `MobileTopBarV4` (z-index 100 inline), desktop `Header`
   * (z-50) — para que su botón de cerrar (la X del <Modal> de @plick/ui, que flota fuera de la
   * esquina superior) sea accesible. Sin esto, en móvil el header fijo tapaba la X (z-50 < 100) y
   * el modal no se podía cerrar.
   *
   * Queda numéricamente por encima de `pedido` (99), pero NO rompe el invariante "lo operativo
   * gana": este modal cede al pedido por DESMONTAJE vía OverlayGate (con `yielding`, `useOverlaySlot`
   * retira el slot y el modal deja de renderizarse), nunca por z-index → jamás es co-visible con el
   * toast de pedido. (Que el header móvil (100) esté por encima de `pedido` (99) es deuda aparte —
   * follow-up separado.)
   */
  announcementModal: 110,
  /** Toast de pedido entrante — lo operativo, SIEMPRE arriba de Paulina. */
  pedido: 99,
  /**
   * Diálogo de "cambios sin guardar" al intentar salir de una página de edición
   * (`DirtyNavModal`). Va DEBAJO del toast de pedido a propósito: es un diálogo
   * bloqueante que el usuario tiene que resolver, así que —a diferencia de
   * `announcementModal`— no puede ceder por desmontaje (desmontarlo dejaría una
   * navegación bloqueada sin diálogo que la resuelva). Como se queda montado, la
   * única forma de respetar "lo operativo SIEMPRE gana" es que el toast lo pise:
   * el diálogo es central y el toast vive abajo a la derecha, así que conviven.
   *
   * Antes esto era un `zIndex: 200` inline, fuera del registro: por encima de
   * TODO, incluido el toast de pedido — justo el invariante por el que el editor
   * de playlists dejó de ser modal.
   */
  dirtyNavModal: 98,
  /** Interruptive prompt (PWA install / notifications) coordinated by the OverlayGate.
      Below the order toast so the operational signal always shows on top. */
  overlayPrompt: 97,
  /** Backdrop behind an interruptive prompt — one tier under its card. */
  overlayBackdrop: 96,
  /** Barra de guardado flotante de una página de edición (móvil). Sobre el contenido
      del formulario, muy por debajo de cualquier capa interruptiva. */
  editSaveBarFloating: 30,
  /** La misma barra en su variante sticky (desktop/tablet). */
  editSaveBarSticky: 25,
  /** Chat de Paulina abierto: panel desktop / bottom-sheet móvil (cede ante pedido). */
  paulinaSheet: 95,
  /** Entrada de Paulina (pill desktop) + coachmark proactivo (debajo del pedido). */
  paulinaPill: 80,
} as const;

export type ZLayer = keyof typeof Z;
